import { Box, Center, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as CheckIcon } from 'assets/icons/check_circle_outline.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close_circle.svg';
import { Code } from 'constants/keyboard';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetWidgetMemberComponent } from 'widgets/Widget.types';
import { useWidget } from 'widgets/sdk';

import { ComponentWidgetIdKeys, MultipleChoicesType, MultipleChoicesWidgetData } from './MultipleChoicesWidget.types';
import ChoiceButton from './components/ChoiceButton';
import { SubmitButton } from './components/SubmitButton';

type MultipleChoicesProps = {
  getWidgetMemberComponent: GetWidgetMemberComponent;
  isReadOnly?: boolean;
};

/**
 * Handle the choice selection for the multiple choices widget
 * @returns
 */
export const MultipleChoices = ({ getWidgetMemberComponent, isReadOnly = false }: MultipleChoicesProps) => {
  const { t } = useTranslation('multiple_choices_widget', {
    keyPrefix: 'viewMode',
    useSuspense: false,
  });

  const { componentWidgetIdMap, correctAnswers, selectedAnswers, memberWidgetIds, type } =
    useWidget<MultipleChoicesWidgetData>();
  const [userChoices, setUserChoices] = useState<string[]>(selectedAnswers ?? []);
  const [showResult, setShowResult] = useState(false);
  const questionTextWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const answerWidgetIdList = memberWidgetIds.filter((id) => id !== questionTextWidgetId);

  const showSelectMoreMessage = correctAnswers.length > 1;

  const GroupMemberWidget = getWidgetMemberComponent();

  const isCorrectAll = useMemo(() => {
    if (showResult === false) return false;
    // NOTE: This can be a problem in the future,
    // Now, it's ok but if we use the value as other it can be a problem. e.g.) `'royal blue'.includes('blue') // true`.
    return correctAnswers.length === userChoices.length && correctAnswers.every((value) => userChoices.includes(value));
  }, [showResult, correctAnswers, userChoices]);

  const handleSubmit = useCallback(() => {
    if (!isReadOnly) return;

    // Display the result view
    setShowResult(true);
  }, [isReadOnly, setShowResult]);

  const handleChoiceClick = useCallback(
    (index: number) => {
      if (!isReadOnly) return;

      const isSingleCorrectAnswer = correctAnswers.length === 1;
      const value = index.toString();

      if (isSingleCorrectAnswer) {
        setUserChoices([value]);
      } else {
        setUserChoices((prev) => (prev?.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
      }
      // Reset the result view
      setShowResult(false);
    },
    [isReadOnly, correctAnswers, setUserChoices, setShowResult],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.preventDefault(); // Prevent to focus on the button when it clicked

      handleChoiceClick(index);
    },
    [handleChoiceClick],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === Code.Enter || e.key === Code.Space) {
        handleChoiceClick(index);
      }
    },
    [handleChoiceClick],
  );

  return (
    <Flex flexDirection={'column'} gap={2} w='100%' minW='fit-content'>
      <Flex flexDirection={'column'} pt={4} px={4} gap={1}>
        <GroupMemberWidget widgetId={questionTextWidgetId} isReadOnly={isReadOnly} />

        <Flex w='full' justifyContent='center' opacity={showSelectMoreMessage ? 1 : 0}>
          <Text color='gray.800' fontSize='xs'>
            {t('selectMoreThanOne')}
          </Text>
        </Flex>
      </Flex>

      <Flex
        flexDir={type === MultipleChoicesType.Horizontal ? 'row' : 'column'}
        gap={2.5}
        px={4}
        w='full'
        minW='fit-content'
      >
        {answerWidgetIdList.map((answerId, index) => {
          const includesUserChoice = userChoices.includes(index.toString());
          const includesCorrectAnswer = correctAnswers.includes(index.toString());
          return (
            <ChoiceButton
              key={index}
              isReadOnly={isReadOnly}
              isSelected={includesUserChoice}
              showResult={showResult && (includesUserChoice || includesCorrectAnswer)}
              isCorrect={showResult && includesUserChoice && includesCorrectAnswer}
              onClick={(e) => handleClick(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              <GroupMemberWidget widgetId={answerId} isReadOnly={isReadOnly} />
            </ChoiceButton>
          );
        })}
      </Flex>

      <Box pt={2} p={4} aria-hidden={true}>
        <Center>
          <SubmitButton onSubmit={handleSubmit} />
          {showResult && (
            <Box position='absolute' ml='210px'>
              <Flex alignItems={'center'} gap='0.5'>
                {isCorrectAll ? (
                  <CheckIcon width='18px' height='18px' color={'var(--vg-colors-action-green-500)'} />
                ) : (
                  <CloseIcon width='18px' height='18px' color={'var(--vg-colors-red-600)'} />
                )}
                <Text color={isCorrectAll ? 'action-green.500' : 'red.600'} fontSize={'sm'}>
                  {isCorrectAll ? t('isCorrect') : t('isIncorrect')}
                </Text>
              </Flex>
            </Box>
          )}
        </Center>
      </Box>
    </Flex>
  );
};
